import { inject as service } from '@ember/service';
import { dasherize, underscore } from '@ember/string';
import { isNone, isEmpty } from '@ember/utils';
import JSONSerializer from '@ember-data/serializer/json';
import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import { UNIT_OVERRIDE_SUPPORT } from '@square/dashboard-data/models/catalog/catalog-service-constants';

// eslint-disable-next-line ember/no-classic-classes
export default JSONSerializer.extend(EmbeddedRecordsMixin, {
  attrs: {
    enabledUnits: {
      embedded: 'always'
    },
    disabledUnits: {
      embedded: 'always'
    }
  },
  store: service(),
  keyForAttribute(attr) {
    return underscore(attr).toLowerCase();
  },
  keyForRelationship(key, relationship) {
    if (key === 'enabledUnits' || key === 'disabledUnits') {
      return underscore(key);
    }
    const underscored = underscore(key);
    if (relationship === 'hasMany') {
      // remove the `s` from the end of key
      return `${underscored.slice(0, -1)}_ids`;
    } else {
      return `${underscored}_id`;
    }
  },
  pushPayload(store, payload) {
    const modelClass = this._modelClassForCatalogType(payload.catalog_type);
    const normalized = this.normalizeSingleResponse(store, modelClass, payload);
    store.push(normalized);
  },
  // returns the name of the EmberData model corresponding to the catalog type or null if
  // there is no Dashboard model corresponding to the passed catalog type.
  _modelNameForCatalogType(catalogType) {
    try {
      const modelName = dasherize(catalogType.toLowerCase());
      // `modelFor` will throw an exception if no model exists for the model name
      this.store.modelFor(modelName);
      return modelName;
    } catch {
      return null;
    }
  },
  _modelClassForCatalogType(catalogType) {
    const modelName = this._modelNameForCatalogType(catalogType);
    return modelName ? this.store.modelFor(modelName) : null;
  },
  normalizeForPush(type, object) {
    const omittedAttributeKeys = new Set(['channels']);
    for (const [key] of type.attributes.entries()) {
      const normalizedKey = this.keyForAttribute(key);

      // Empty attributes that are explicitly set to null are not set to the attribute's
      // default value. Adding normalized attributes to the Set will skip setting the
      // attribute to null allowing the default value to be used when saving an empty attribute.
      if (omittedAttributeKeys.has(normalizedKey)) {
        continue;
      }
      if (!Object.keys(object).includes(normalizedKey)) {
        object[normalizedKey] = null;
      }
    }
    return this.normalize(type, object);
  },
  normalize(type, object) {
    const omittableAttributeKeys = new Set(['channels']);
    const normalized = this._super(type, object);
    for (const [key] of type.attributes.entries()) {
      const normalizedKey = this.keyForAttribute(key);
      if (omittableAttributeKeys.has(normalizedKey) && normalized.data.attributes[normalizedKey] === undefined) {
        normalized.data.attributes[normalizedKey] = type.attributes.get(key).options.defaultValue;
      }
    }
    if (object.relatedObjects) {
      normalized.included = this._normalizeRelatedObjects(object.relatedObjects);
    }
    return normalized;
  },
  _normalizeRelatedObjects(objects) {
    return objects.map(object => {
      const modelClass = this._modelClassForCatalogType(object.catalog_type);
      const serializer = this.store.serializerFor(modelClass.modelName);
      return serializer.normalize(modelClass, object).data;
    });
  },
  serialize(snapshot) {
    const json = this._super(snapshot, {
      includeId: true
    });
    if (!json.id && snapshot.record.isNew) {
      json.id = snapshot.record.idOrTempId();
    }
    json.catalog_type = snapshot.type.catalogType();
    this._cleanUpUnitOverrideAttributes(json, snapshot, snapshot.type.catalogType());
    return json;
  },
  serializeBelongsTo(snapshot, json, relationship) {
    this._super(snapshot, json, relationship);
    const payloadKey = this.keyForRelationship(relationship.key, 'belongsTo', 'serialize');
    if (!json[payloadKey]) {
      const relatedSnapshot = snapshot.belongsTo(relationship.key);
      if (relatedSnapshot && relatedSnapshot.record && relatedSnapshot.record.isNew) {
        json[payloadKey] = relatedSnapshot.record.idOrTempId();
      }
    }
  },
  serializeHasMany(snapshot, json, relationship) {
    this._super(snapshot, json, relationship);
    const payloadKey = this.keyForRelationship(relationship.key, 'hasMany', 'serialize');
    if (json[payloadKey]) {
      const relatedSnapshot = snapshot.hasMany(relationship.key);
      relatedSnapshot?.forEach((x, i) => {
        if (x?.record?.isNew && x.record.idOrTempId) {
          json[payloadKey][i] = x.record.idOrTempId();
        }
      });
    }
  },
  // _cleanUpUnitOverrideAttributes removes unit override attributes that could not have been created
  // by dashboard. This way, when the user saves, the object will actually have the values that the
  // dashboard UI suggests that it does.
  // Attributes that have first class support for unit overrides in dashboard will
  // not be cleared. The list of attributes and object types that are allowed to have
  // overrides is stored in UNIT_OVERRIDE_SUPPORT in catalog_service_constants.
  _cleanUpUnitOverrideAttributes(recordJson, snapshot, catalogType) {
    if (!isEmpty(Object.keys(recordJson.unit_overrides))) {
      // Only remove overrides attributes that are dirty.
      Object.keys(snapshot.changedAttributes()).forEach(dirtyAttr => {
        // Overrides are supported for these attributes, don't remove even if dirty
        if (isNone(UNIT_OVERRIDE_SUPPORT[catalogType]) || !UNIT_OVERRIDE_SUPPORT[catalogType].includes(dirtyAttr)) {
          for (const unitToken in recordJson.unit_overrides) {
            const overrides = recordJson.unit_overrides[unitToken];
            delete overrides[underscore(dirtyAttr)];
          }
        }
      });

      // Remove units with no overrides
      for (const unitToken in recordJson.unit_overrides) {
        const overrides = recordJson.unit_overrides[unitToken];
        if (isEmpty(Object.keys(overrides))) {
          delete recordJson.unit_overrides[unitToken];
        }
      }
    }

    // Since you can't actually edit the enabled attribute for an object type that doesn't
    // support overrides of the enabled attribute, modifying the object should be considered a
    // modification to the enabled type
    if (isNone(UNIT_OVERRIDE_SUPPORT[catalogType]) || !UNIT_OVERRIDE_SUPPORT[catalogType].includes('enabled')) {
      recordJson.enabled_units = [];
      recordJson.disabled_units = [];
    }
  }
});